import React, { useState, useEffect } from 'react';
import Icon from '../../shared/Icon';
import { apiService, DEFAULT_SUPPORTED_FILE_TYPES } from '../../../../services/api';
import FileUploadZone from '../../shared/FileUploadZone';
import './Step2ConnectRawData.css';

/**
 * View Component for Step 2: Connect Raw Data (Upload Files)
 * Supported file types are loaded from backend config (GET /api/v1/config/supported-file-types).
 */
const Step2ConnectRawData = ({ datasetId, selectedFiles: initialSelectedFiles, onUploadFiles, onSetSelectedFiles, formData, onSetFormData }) => {
  const [selectedFiles, setSelectedFiles] = useState(initialSelectedFiles || []);
  const [error, setError] = useState(null);
  const [selectedSource, setSelectedSource] = useState(
    () => formData?.connectionData?.selectedSource || 'local',
  ); // 'local', 'connection'
  const [connectionType, setConnectionType] = useState(
    () => formData?.connectionData?.connectionType || '',
  ); // 'azure_blob', 'aws_s3', 'gcs'
  const [storageAccount, setStorageAccount] = useState(
    () => formData?.connectionData?.storageAccount || '',
  );
  const [containerName, setContainerName] = useState(
    () => formData?.connectionData?.containerName || '',
  );
  const [sasToken, setSasToken] = useState(
    () => formData?.connectionData?.sasToken || '',
  );
  const [pathPrefix, setPathPrefix] = useState(
    () => formData?.connectionData?.pathPrefix || '',
  );
  const [supportedFileTypes, setSupportedFileTypes] = useState(DEFAULT_SUPPORTED_FILE_TYPES);

  // Load supported file types from backend (single source of truth)
  useEffect(() => {
    let cancelled = false;
    apiService.getSupportedFileTypes().then((data) => {
      if (!cancelled) setSupportedFileTypes(data);
    });
    return () => { cancelled = true; };
  }, []);

  // Sync selectedFiles when the prop changes (e.g., parent reset). Connection
  // state is intentionally NOT re-synced here — it's seeded lazily on mount and
  // owned by this component thereafter. Re-syncing on every formData change
  // caused the post-mount flicker described above.
  useEffect(() => {
    if (initialSelectedFiles) {
      setSelectedFiles(initialSelectedFiles);
    }
  }, [initialSelectedFiles]);

  const handleFilesChanged = (updatedFiles) => {
    setSelectedFiles(updatedFiles);
    if (onSetSelectedFiles) {
      onSetSelectedFiles(updatedFiles);
    }
    setError(null);
  };

  // Store connection data in formData when user makes selections
  useEffect(() => {
    if (onSetFormData && formData) {
      onSetFormData({
        ...formData,
        connectionData: {
          selectedSource,
          connectionType,
          storageAccount,
          containerName,
          sasToken,
          pathPrefix
        }
      });
    }
  }, [selectedSource, connectionType, storageAccount, containerName, sasToken, pathPrefix]);


  return (
    <div className="step2-upload-container">
      <h3 className="step2-upload-title">Connect data</h3>
      <p className="step2-upload-subtitle">Choose where this dataset's raw files will come from.</p>

      <div className="step2-upload-options">
        <div
          className={`step2-upload-option ${selectedSource === 'local' ? 'step2-upload-option-selected' : ''}`}
          onClick={() => {
            setSelectedSource('local');
            setError(null);
          }}
          style={{ cursor: 'pointer' }}
        >
          <Icon name="upload_file" className="step2-upload-icon" size={32} />
          <div className="step2-upload-option-content">
            <div className="step2-upload-option-title">Upload files</div>
          </div>
        </div>

        <div
          className={`step2-upload-option ${selectedSource === 'connection' ? 'step2-upload-option-selected' : ''}`}
          onClick={() => {
            setSelectedSource('connection');
            setError(null);
          }}
          style={{ cursor: 'pointer' }}
        >
          <Icon name="cloud" className="step2-upload-icon" size={32} />
          <div className="step2-upload-option-content">
            <div className="step2-upload-option-title">Connection</div>
          </div>
        </div>
      </div>

      {selectedSource === 'connection' && (
        <div className="step2-connection-config">
          <div className="step2-connection-field">
            <label className="step2-connection-label">
              Connection type<span className="step2-required">*</span>
            </label>
            <select
              className="resui-select"
              value={connectionType}
              onChange={(e) => {
                setConnectionType(e.target.value);
                setError(null);
              }}
            >
              <option value="">Select connection type</option>
              <option value="azure_blob">Azure Blob Storage</option>
              <option value="aws_s3">AWS S3</option>
              <option value="gcs">Google Cloud Storage</option>
            </select>
          </div>

          {connectionType === 'azure_blob' && (
            <>
              <div className="step2-connection-field">
                <label className="step2-connection-label">
                  Storage account name<span className="step2-required">*</span>
                </label>
                <input
                  type="text"
                  className="resui-input"
                  placeholder="Enter storage account name"
                  value={storageAccount}
                  onChange={(e) => {
                    setStorageAccount(e.target.value);
                    setError(null);
                  }}
                />
                <p className="step2-connection-helper">Your Azure storage account name.</p>
              </div>
              <div className="step2-connection-field">
                <label className="step2-connection-label">
                  Container name<span className="step2-required">*</span>
                </label>
                <input
                  type="text"
                  className="resui-input"
                  placeholder="my-container"
                  value={containerName}
                  onChange={(e) => {
                    setContainerName(e.target.value);
                    setError(null);
                  }}
                />
              </div>
              <div className="step2-connection-field">
                <label className="step2-connection-label">
                  Connection string or SAS token<span className="step2-required">*</span>
                </label>
                <input
                  type="text"
                  className="resui-input"
                  placeholder="Enter connection string or SAS token"
                  value={sasToken}
                  onChange={(e) => {
                    setSasToken(e.target.value);
                    setError(null);
                  }}
                />
                <p className="step2-connection-helper">Secure authentication credentials for your storage account.</p>
              </div>
              <div className="step2-connection-field">
                <label className="step2-connection-label">
                  Path prefix (optional)
                </label>
                <input
                  type="text"
                  className="resui-input"
                  placeholder="test"
                  value={pathPrefix}
                  onChange={(e) => {
                    setPathPrefix(e.target.value);
                    setError(null);
                  }}
                />
                <p className="step2-connection-helper">Subfolder path inside the container.</p>
              </div>
            </>
          )}
        </div>
      )}

      {selectedSource === 'local' && (
        <FileUploadZone
          selectedFiles={selectedFiles}
          onFilesChanged={handleFilesChanged}
          supportedFileTypes={supportedFileTypes}
        />
      )}


    </div>
  );
};

export default Step2ConnectRawData;
